import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useStreamAuth } from '../hooks/useStreamAuth';
import subscriptionService from '../services/SubscriptionService';
import { safeUserMessage } from '../utils/errorMessages';
import { formatMoney, formatPeriod } from '../utils/money';
import { clearPaywallReturnTo, readPaywallReturnTo } from '../utils/returnTo';
import { SECURED_FIELD_STYLES } from '../utils/adyen';
import Footer from '../components/common/Footer';
import '../styles/adyen-overrides.css';

// `/v1/offers` price for the viewer's country. Once an order exists its total is what is charged
// (a country with no price row of its own is shown the base price and converted at order time).
const formatPlanPrice = plan => plan?.price_display || '—';

const PHASE_SELECT = 'select';
const PHASE_PAYMENT = 'payment';
const PHASE_VERIFYING = 'verifying';
const SESSION_KEY_ORDER_ID = 'bkfc_pending_order_id';
// The finalize call echoes the offer being bought, so it survives the 3DS redirect too.
const SESSION_KEY_OFFER_ID = 'bkfc_pending_cleeng_offer_id';
// Shared between the onSubmit non-Authorised path and onPaymentFailed so both
// sides of a declined payment show the same copy for the same Adyen resultCode.
const ADYEN_RESULT_MESSAGES = {
  Refused: 'Your payment was declined. Please try a different payment method.',
  Cancelled: 'Payment was cancelled.',
  Error: 'A payment error occurred. Please try again.',
};
// The only statuses that mean the charge didn't go through. Everything else
// (Authorised/Authorized, Pending, activationDelayed, ...) is a success from
// the bridge's point of view, even if success isn't explicitly true.
const DECLINE_STATUSES = new Set(Object.keys(ADYEN_RESULT_MESSAGES).map(s => s.toLowerCase()));
// Apple Pay / Google Pay are temporarily disabled until Cleeng enables the wallet
// payment methods for our account. The orders API currently returns no
// merchantIdentifier / gatewayMerchantId, so Adyen's applePay/sessions validation
// 500s ("Required field 'merchantIdentifier' is null") and Google Pay can't init.
// Set back to true once Cleeng returns those fields — no other change needed.
const WALLET_PAYMENTS_ENABLED = false;
const PAYWALL_FEATURES = [
  'Monthly live events',
  'Access to full BKFC library',
  'Behind the scenes content',
  'Original series',
  'Additional fights from around the world',
  'Fast pass access at in-person BKFC events',
  'Merchandise discounts',
];
// GEO_BLOCKED (403) carries the server's own copy for the viewer's country: show it as written.
const checkoutErrorMessage = (err, fallback) =>
  err?.code === 'GEO_BLOCKED' && err.message ? err.message : safeUserMessage(err, fallback);

const PaywallPage = () => {
  const navigate = useNavigate();
  const { logout } = useStreamAuth();
  const { products, isLoadingProducts, fetchProducts, error: productsError } = useSubscription();
  // Read at first render: the 3DS return strips the query below, so `returnTo` also rides in
  // sessionStorage across the redirect.
  const [returnTo] = useState(() => readPaywallReturnTo(window.location.search));
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [phase, setPhase] = useState(PHASE_SELECT);
  const [orderData, setOrderData] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const dropinContainerRef = useRef(null);
  const dropinInstanceRef = useRef(null);
  const orderIdRef = useRef(null);
  const productRef = useRef(null);
  // True only after the bridge said the charge on this order was not made
  // (Refused/Cancelled/Error). The next submit then mints a fresh order. An
  // UNKNOWN outcome (network error) keeps the same order, so a retry of a charge
  // that actually went through is refused by Cleeng instead of charged twice.
  const orderDeclinedRef = useRef(false);
  const successHandledRef = useRef(false);

  // Leave for `returnTo` (else home) after a successful purchase. The entitlement can lag the
  // charge (Cleeng activates asynchronously; /v1/me/entitlement answers 503 "confirming your
  // purchase" meanwhile), so give it a moment to settle first. Guarded to run once per payment.
  const confirmAndGoHome = useCallback(async () => {
    if (successHandledRef.current) {
      return;
    }
    successHandledRef.current = true;

    setPaymentError(null);
    setPhase(PHASE_VERIFYING);

    for (let attempt = 0; attempt < 6; attempt += 1) {
      if (await subscriptionService.hasActiveSubscription()) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    sessionStorage.removeItem(SESSION_KEY_ORDER_ID);
    sessionStorage.removeItem(SESSION_KEY_OFFER_ID);
    clearPaywallReturnTo();
    // Hard redirect (not SPA navigate): a fresh app load, so no in-memory state from before the
    // purchase decides what the viewer sees next.
    window.location.assign(returnTo || '/');
  }, [returnTo]);

  // Callback-independent safety net: once a payment has been submitted, poll for the
  // subscription becoming active and leave the paywall the moment it does. This covers
  // cases where Adyen's onSubmit/onPaymentCompleted flow doesn't drive the redirect.
  useEffect(() => {
    if (!paymentSubmitted) {
      return undefined;
    }
    let stopped = false;
    const interval = setInterval(async () => {
      const active = await subscriptionService.hasActiveSubscription();
      if (!stopped && active) {
        clearInterval(interval);
        confirmAndGoHome();
      }
    }, 3000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [paymentSubmitted, confirmAndGoHome]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectResult = params.get('redirectResult');
    const storedOrderId = sessionStorage.getItem(SESSION_KEY_ORDER_ID);
    const storedOfferId = sessionStorage.getItem(SESSION_KEY_OFFER_ID);

    if (redirectResult && storedOrderId) {
      window.history.replaceState({}, '', window.location.pathname);
      setPhase(PHASE_VERIFYING);
      subscriptionService
        .finalizePayment({
          orderId: Number(storedOrderId),
          cleengOfferId: storedOfferId,
          details: { redirectResult },
        })
        .then(response => {
          if (response?.success === false) {
            sessionStorage.removeItem(SESSION_KEY_ORDER_ID);
            setPaymentError(
              ADYEN_RESULT_MESSAGES[response.status] || 'Payment was not successful.',
            );
            setPhase(PHASE_SELECT);
            return;
          }
          confirmAndGoHome();
        })
        .catch(err => {
          sessionStorage.removeItem(SESSION_KEY_ORDER_ID);
          setPaymentError(
            safeUserMessage(err, "We couldn't verify your payment. Please try again."),
          );
          setPhase(PHASE_SELECT);
        });
    }
  }, [navigate, confirmAndGoHome]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSelectPlan = useCallback(async product => {
    setSelectedProduct(product);
    setIsCreatingOrder(true);
    setPaymentError(null);

    try {
      const response = await subscriptionService.createOrder(product);
      setOrderData(response);
      orderIdRef.current = response.order?.id;
      productRef.current = product;
      orderDeclinedRef.current = false;
      setPhase(PHASE_PAYMENT);
    } catch (err) {
      setPaymentError(checkoutErrorMessage(err, "We couldn't start your order. Please try again."));
    } finally {
      setIsCreatingOrder(false);
    }
  }, []);

  useEffect(() => {
    if (phase !== PHASE_PAYMENT || !orderData?.paymentGateway || !dropinContainerRef.current) {
      return;
    }

    let mounted = true;

    const initDropin = async () => {
      try {
        const { AdyenCheckout, Dropin } = await import('@adyen/adyen-web/auto');
        await import('@adyen/adyen-web/styles/adyen.css');

        if (!mounted) {
          return;
        }

        const { paymentGateway } = orderData;

        // While wallet payments are disabled, drop applepay/googlepay from the list
        // handed to the Drop-in so the buttons don't render (and can't trigger the
        // failing Adyen validation). Card and other methods are unaffected.
        const paymentMethods = WALLET_PAYMENTS_ENABLED
          ? paymentGateway.paymentMethods
          : (paymentGateway.paymentMethods || []).filter(
              pm => pm.type !== 'applepay' && pm.type !== 'googlepay',
            );

        const configuration = {
          clientKey: paymentGateway.clientKey,
          environment: paymentGateway.environment || 'test',
          amount: paymentGateway.amount,
          countryCode: paymentGateway.countryCode || 'US',
          paymentMethodsResponse: { paymentMethods },
          locale: 'en-US',

          onSubmit: async (state, component, actions) => {
            setIsProcessing(true);
            setPaymentError(null);
            setPaymentSubmitted(true);
            try {
              if (orderDeclinedRef.current) {
                const fresh = await subscriptionService.createOrder(productRef.current);
                orderIdRef.current = fresh.order?.id;
                orderDeclinedRef.current = false;
              }
              const orderId = orderIdRef.current;
              const cleengOfferId = productRef.current?.cleeng_offer_id;
              sessionStorage.setItem(SESSION_KEY_ORDER_ID, String(orderId));
              sessionStorage.setItem(SESSION_KEY_OFFER_ID, cleengOfferId);
              const { paymentMethod, browserInfo, billingAddress } = state.data;
              const response = await subscriptionService.submitPayment({
                orderId,
                cleengOfferId,
                paymentMethodData: paymentMethod,
                returnUrl: window.location.href,
                origin: window.location.origin,
                browserInfo,
                billingAddress,
              });

              if (response.action) {
                actions.resolve({
                  resultCode: response.status || 'RedirectShopper',
                  action: response.action,
                });
              } else if (
                response.success === false ||
                (response.status && DECLINE_STATUSES.has(response.status.toLowerCase()))
              ) {
                // No 3DS step, but the bridge told us the charge didn't go through
                // (e.g. { status: 'Refused', action: null, success: false }). Show the
                // decline directly rather than relying on Adyen's onPaymentFailed firing
                // off our own resolve() — same belt-and-suspenders reasoning as the
                // success path above not trusting onPaymentCompleted.
                sessionStorage.removeItem(SESSION_KEY_ORDER_ID);
                orderDeclinedRef.current = Boolean(ADYEN_RESULT_MESSAGES[response.status]);
                setPaymentError(
                  ADYEN_RESULT_MESSAGES[response.status] || 'Payment was not successful.',
                );
                try {
                  actions.resolve({ resultCode: response.status || 'Refused' });
                } catch (resolveErr) {
                  console.warn(
                    '[Paywall] adyen resolve after decline failed (non-fatal)',
                    resolveErr,
                  );
                }
              } else {
                // Payment succeeded with no 3DS step. Drive the redirect from here
                // rather than relying on Adyen's onPaymentCompleted callback (which
                // does not reliably fire in this Drop-in setup, leaving the user
                // stranded on the paywall). Trigger the redirect first, then best-effort
                // tell Adyen — so a flaky resolve can't block navigation.
                confirmAndGoHome();
                try {
                  actions.resolve({ resultCode: 'Authorised' });
                } catch (resolveErr) {
                  console.warn(
                    '[Paywall] adyen resolve after success failed (non-fatal)',
                    resolveErr,
                  );
                }
              }
            } catch (err) {
              setPaymentError(checkoutErrorMessage(err, 'Payment failed. Please try again.'));
              actions.reject();
            } finally {
              setIsProcessing(false);
            }
          },

          onAdditionalDetails: async (state, component, actions) => {
            setIsProcessing(true);
            setPaymentError(null);
            setPaymentSubmitted(true);
            try {
              const response = await subscriptionService.finalizePayment({
                orderId: orderIdRef.current,
                cleengOfferId: productRef.current?.cleeng_offer_id,
                details: state.data.details,
              });

              if (response.success) {
                actions.resolve({ resultCode: 'Authorised' });
                confirmAndGoHome();
              } else {
                actions.reject();
                setPaymentError('Payment verification failed. Please try again.');
              }
            } catch (err) {
              setPaymentError(
                safeUserMessage(err, "We couldn't finalize your payment. Please try again."),
              );
              actions.reject();
            } finally {
              setIsProcessing(false);
            }
          },

          onPaymentCompleted: () => {
            confirmAndGoHome();
          },

          // Adyen calls this right after switching the Drop-in to its final
          // error screen (red X, "An unknown error occurred", no card form).
          // The banner above is the error message; put the form back so the
          // viewer can try again without leaving the page.
          onPaymentFailed: result => {
            if (result?.resultCode) {
              setPaymentError(
                ADYEN_RESULT_MESSAGES[result.resultCode] || 'Payment was not successful.',
              );
            }
            dropinInstanceRef.current?.setStatus('ready');
          },

          onError: error => {
            if (error.name === 'CANCEL') {
              return;
            }
            setPaymentError(
              safeUserMessage(error, 'Something went wrong with your payment. Please try again.'),
            );
          },
        };

        const applePayMethod = paymentMethods.find(pm => pm.type === 'applepay');
        const googlePayMethod = paymentMethods.find(pm => pm.type === 'googlepay');

        const pmConfig = {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            styles: SECURED_FIELD_STYLES,
          },
        };

        if (applePayMethod) {
          pmConfig.applepay = {
            amount: paymentGateway.amount,
            countryCode: paymentGateway.countryCode || 'US',
            configuration: {
              merchantName: paymentGateway.shopperStatement,
              merchantIdentifier: applePayMethod.configuration?.merchantIdentifier,
            },
            buttonType: 'plain',
            buttonColor: 'black',
          };
        }

        if (googlePayMethod) {
          pmConfig.googlepay = {
            amount: paymentGateway.amount,
            countryCode: paymentGateway.countryCode || 'US',
            environment: paymentGateway.environment === 'test' ? 'TEST' : 'PRODUCTION',
            configuration: {
              merchantName: paymentGateway.shopperStatement,
              gatewayMerchantId: googlePayMethod.configuration?.gatewayMerchantId,
              merchantId: googlePayMethod.configuration?.merchantId,
            },
          };
        }

        const checkout = await AdyenCheckout(configuration);
        const dropin = new Dropin(checkout, {
          paymentMethodsConfiguration: pmConfig,
        });

        if (mounted && dropinContainerRef.current) {
          dropin.mount(dropinContainerRef.current);
          dropinInstanceRef.current = dropin;
        }
      } catch (err) {
        if (mounted) {
          setPaymentError('Failed to initialize payment form. Please refresh and try again.');
        }
      }
    };

    initDropin();

    return () => {
      mounted = false;
      if (dropinInstanceRef.current) {
        try {
          dropinInstanceRef.current.unmount();
        } catch {
          // ignore unmount errors
        }
        dropinInstanceRef.current = null;
      }
    };
  }, [phase, orderData, navigate, confirmAndGoHome]);

  const handleBack = () => {
    if (dropinInstanceRef.current) {
      try {
        dropinInstanceRef.current.unmount();
      } catch {
        // ignore
      }
      dropinInstanceRef.current = null;
    }
    setPhase(PHASE_SELECT);
    setOrderData(null);
    setPaymentError(null);
    setSelectedProduct(null);
  };

  const handleLogout = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  }, [logout]);

  if (isLoadingProducts) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-white text-lg">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-white flex flex-col">
      <header className="py-4 px-6 sticky top-0 z-30 bg-gradient-to-b from-primary via-primary/95 to-primary/80">
        <div className="container flex items-center justify-between mx-auto">
          <div className="flex items-center">
            <img
              src="/assets/images/bkfc_logo.webp"
              alt="BKFC"
              className="h-7 md:h-8 w-auto grayscale brightness-0 invert"
            />
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="font-display text-sm uppercase tracking-wide text-white/70 hover:text-accent bg-transparent border-0 p-0 cursor-pointer transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/images/Subscribeloginboxer.png"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-black/80 to-primary" />
        </div>

        <div className="relative container mx-auto px-6 pt-12 pb-8 sm:pt-16 sm:pb-10 lg:pt-20 lg:pb-12 text-center">
          <h1 className="text-accent text-3xl sm:text-5xl lg:text-6xl font-heading uppercase tracking-wide">
            Best Deal In Combat Sports
          </h1>
          <p className="text-text-muted text-sm sm:text-base uppercase tracking-wide mt-3 mb-10">
            Subscription works on all apps and devices
          </p>

          <div className="max-w-2xl mx-auto border border-accent/25 rounded-xl bg-black/30 px-6 py-4 sm:px-8 sm:py-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
              {PAYWALL_FEATURES.map((feature, i) => (
                <div
                  key={feature}
                  className={`flex items-center gap-3 py-2.5 ${
                    i < PAYWALL_FEATURES.length - 1 ? 'border-b border-white/10' : ''
                  }`}
                >
                  <img src="/assets/images/check_small.png" alt="" className="w-4 h-4 shrink-0" />
                  <span className="text-sm sm:text-base text-gray-200 font-heading uppercase tracking-wide text-left">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="container mx-auto px-6 pt-4 pb-10 sm:pt-6 sm:pb-14">
          <div className="w-full max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-heading uppercase tracking-wide">
                {phase === PHASE_VERIFYING
                  ? 'Verifying Payment'
                  : phase === PHASE_SELECT
                    ? 'Choose Your Plan'
                    : 'Complete Payment'}
              </h2>
              <p className="text-text-muted mt-2">
                {phase === PHASE_VERIFYING
                  ? 'Please wait while we confirm your payment with your bank.'
                  : phase === PHASE_SELECT
                    ? 'Subscribe to unlock all BKFC content'
                    : `Subscribing to ${selectedProduct?.name || 'BKFC+'}`}
              </p>
            </div>

            {(productsError || paymentError) && (
              <div className="mb-6 bg-red-950/40 border border-red-900 rounded-lg px-4 py-3 text-sm text-red-400">
                {productsError || paymentError}
              </div>
            )}

            {phase === PHASE_VERIFYING && (
              <div className="text-center py-16">
                <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-text-muted text-sm">
                  This may take a moment. Please do not close this page.
                </p>
              </div>
            )}

            {phase === PHASE_SELECT && (
              <div className="space-y-4">
                {products.length === 0 && !isLoadingProducts && (
                  <div className="text-center text-text-muted py-8">
                    No subscription plans are currently available.
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {products.map(product => (
                    <button
                      key={product._id}
                      onClick={() => handleSelectPlan(product)}
                      disabled={isCreatingOrder}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all
                        ${
                          isCreatingOrder && selectedProduct?._id === product._id
                            ? 'border-accent bg-accent/10 opacity-75'
                            : 'border-gray-border bg-secondary hover:border-accent hover:bg-accent/5'
                        }
                        disabled:cursor-not-allowed`}
                    >
                      <h3 className="text-lg font-semibold mb-1.5">{product.name}</h3>

                      <div className="flex items-baseline gap-1 mb-0.5">
                        <span className="text-2xl font-bold text-accent">
                          {formatPlanPrice(product)}
                        </span>
                        <span className="text-sm text-text-muted">
                          {formatPeriod(product.period)}
                        </span>
                      </div>
                      {product.trial_days && (
                        <p className="text-sm text-text-muted">
                          {product.trial_days}-day free trial
                        </p>
                      )}

                      {isCreatingOrder && selectedProduct?._id === product._id && (
                        <div className="absolute inset-0 rounded-xl flex items-center justify-center bg-secondary/80">
                          <span className="text-accent text-sm">Setting up payment...</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {phase === PHASE_PAYMENT && (
              <div>
                <button
                  onClick={handleBack}
                  disabled={isProcessing}
                  className="mb-6 text-sm text-text-muted hover:text-white transition-colors disabled:opacity-50"
                >
                  &larr; Back to plan selection
                </button>

                {selectedProduct && (
                  <div className="mb-5 p-3 rounded-lg bg-secondary border border-gray-border flex items-center justify-between">
                    <div>
                      <span className="font-semibold">{selectedProduct.name}</span>
                    </div>
                    <span className="text-accent font-bold">
                      {orderData?.order?.amount != null
                        ? formatMoney(orderData.order.amount, orderData.order.currency)
                        : formatPlanPrice(selectedProduct)}
                    </span>
                  </div>
                )}

                <div
                  ref={dropinContainerRef}
                  className="adyen-dropin-container rounded-lg overflow-hidden"
                />

                {isProcessing && (
                  <div className="mt-3 text-center text-text-muted text-sm">
                    Processing your payment...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaywallPage;
